import { messageIsRaisedWorkflow, messageIsTaskWorkItem } from './reactionConfig';

export const MEETING_PROVIDERS = [
  { id: 'zoom', label: 'Zoom' },
  { id: 'google_meet', label: 'Google Meet' },
  { id: 'teams', label: 'Microsoft Teams' },
  { id: 'custom', label: 'Custom link' },
  { id: 'livekit', label: 'Direct call' },
];

export const getMeetingProviderLabel = (provider) =>
  MEETING_PROVIDERS.find((p) => p.id === provider)?.label || 'Meeting';

export const isDirectCallMeeting = (meetingInfo) =>
  Boolean(
    meetingInfo
    && (meetingInfo.provider === 'livekit'
      || meetingInfo.mode === 'direct'
      || (meetingInfo.joinUrl || '').startsWith('workloop://livekit/')),
  );

export const getLiveKitRoomName = (meetingInfo) => {
  if (!meetingInfo) return null;
  if (meetingInfo.roomName) return meetingInfo.roomName;
  const url = meetingInfo.joinUrl || '';
  if (url.startsWith('workloop://livekit/')) {
    return url.slice('workloop://livekit/'.length);
  }
  return null;
};

/**
 * Build a join URL from provider + meeting ID or pasted link.
 */
export const resolveMeetingJoinUrl = (provider, input) => {
  const trimmed = (input || '').trim();
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed.replace(/&amp;/g, '&');
  }

  const id = trimmed.replace(/\s+/g, '');

  switch (provider) {
    case 'zoom':
      return `https://zoom.us/j/${id}`;
    case 'google_meet':
      return `https://meet.google.com/${id}`;
    case 'teams':
      return `https://teams.microsoft.com/l/meetup-join/${id}`;
    default:
      return trimmed.includes('.') ? `https://${trimmed}` : null;
  }
};

/** Collect saved meeting links from user profile socialLinks. */
export const getSavedMeetingLinks = (socialLinks = {}) => {
  const links = [];
  if (socialLinks.zoom?.trim()) {
    links.push({ id: 'saved-zoom', label: 'My Zoom link', url: socialLinks.zoom.trim(), provider: 'zoom' });
  }
  if (socialLinks.googleMeet?.trim()) {
    links.push({
      id: 'saved-meet',
      label: 'My Google Meet link',
      url: socialLinks.googleMeet.trim(),
      provider: 'google_meet',
    });
  }
  (socialLinks.customLinks || []).forEach((link, index) => {
    const url = (link?.url || '').trim();
    const title = (link?.title || '').trim() || `Custom link ${index + 1}`;
    if (!url) return;
    const lower = `${title} ${url}`.toLowerCase();
    const provider = lower.includes('teams') ? 'teams'
      : lower.includes('zoom') ? 'zoom'
      : lower.includes('meet.google') ? 'google_meet'
      : 'custom';
    links.push({ id: `saved-custom-${index}`, label: title, url, provider });
  });
  return links;
};

export const isMeetingActive = (meetingInfo) =>
  meetingInfo?.status !== 'CLOSED'
  && meetingInfo?.status !== 'SUMMARY'
  && Boolean(meetingInfo?.joinUrl || isDirectCallMeeting(meetingInfo));

export const isMeetingClosed = (meetingInfo) =>
  meetingInfo?.status === 'CLOSED';

export const buildClosedMeetingMessage = (message, userId) => ({
  ...message,
  meetingInfo: {
    ...message.meetingInfo,
    status: 'CLOSED',
    closedAt: new Date().toISOString(),
    closedByUserId: userId ?? message.meetingInfo?.closedByUserId,
  },
});

export const isAlreadyClosedError = (err) => {
  const body = err?.response?.data;
  if (typeof body !== 'string') return false;
  return /already closed/i.test(body.replace(/^Error:\s*/i, ''));
};

export const isMeetingSummary = (meetingInfo) =>
  meetingInfo?.status === 'SUMMARY';

export const getMeetingNoteMessages = (messages = [], meetingMessageId) =>
  messages.filter((m) => m.linkedMeetingMessageId === meetingMessageId);

export const isCallMeetingMessage = (msg) =>
  Boolean(msg?.meetingInfo?.joinUrl || isDirectCallMeeting(msg?.meetingInfo));

export const isCallNoteMessage = (msg) => Boolean(msg?.linkedMeetingMessageId);

const getMessageTime = (msg) =>
  new Date(msg?.createdAt || msg?.timestamp || 0).getTime();

/** Task/workflow cards and activity lines stay separate so hiding notices does not glue tickets together. */
export const isStandaloneTimelineCard = (msg) => {
  if (!msg) return false;
  if (msg.notificationInfo) return true;
  const reactions = msg.reactions || [];
  return messageIsRaisedWorkflow(reactions) || messageIsTaskWorkItem(reactions);
};

/**
 * Build timeline groups: each call = [call card, ...call notes], regular messages grouped by sender/time.
 * Call groups sort by latest activity so new notes keep the group at the bottom.
 */
export const buildTimelineMessageGroups = (messages = []) => {
  if (!messages.length) return [];

  const sorted = [...messages].sort((a, b) => getMessageTime(a) - getMessageTime(b));

  const notesByMeeting = new Map();
  sorted.forEach((m) => {
    if (!m.linkedMeetingMessageId) return;
    const list = notesByMeeting.get(m.linkedMeetingMessageId) || [];
    list.push(m);
    notesByMeeting.set(m.linkedMeetingMessageId, list);
  });

  const consumed = new Set();
  const units = [];

  sorted.forEach((m) => {
    if (m.meetingInfo?.status === 'SUMMARY') {
      consumed.add(m.id);
      return;
    }
    if (!isCallMeetingMessage(m)) return;

    const notes = (notesByMeeting.get(m.id) || []).sort(
      (a, b) => getMessageTime(a) - getMessageTime(b),
    );
    notes.forEach((n) => consumed.add(n.id));
    consumed.add(m.id);

    const groupMessages = [m, ...notes];
    const sortTime = Math.max(...groupMessages.map(getMessageTime));
    units.push({ sortTime, messages: groupMessages, isCallGroup: true });
  });

  const regular = sorted.filter((m) => !consumed.has(m.id));
  const GROUP_TIME_THRESHOLD = 2 * 60 * 1000;
  let currentGroup = [];

  const flushRegular = () => {
    if (currentGroup.length) {
      const sortTime = getMessageTime(currentGroup[currentGroup.length - 1]);
      units.push({ sortTime, messages: [...currentGroup], isCallGroup: false });
      currentGroup = [];
    }
  };

  regular.forEach((message) => {
    const messageTime = getMessageTime(message);
    if (!currentGroup.length) {
      currentGroup = [message];
      return;
    }
    const lastMessage = currentGroup[currentGroup.length - 1];
    const lastMessageTime = getMessageTime(lastMessage);
    const shouldGroup =
      !isStandaloneTimelineCard(lastMessage) &&
      !isStandaloneTimelineCard(message) &&
      lastMessage.senderId === message.senderId &&
      (!!lastMessage.replyToMessageId === !!message.replyToMessageId) &&
      messageTime - lastMessageTime < GROUP_TIME_THRESHOLD &&
      messageTime >= lastMessageTime;

    if (shouldGroup) {
      currentGroup.push(message);
    } else {
      flushRegular();
      currentGroup = [message];
    }
  });
  flushRegular();

  units.sort((a, b) => a.sortTime - b.sortTime);
  return units.map((u) => ({
    messages: u.messages,
    isCallGroup: u.isCallGroup,
  }));
};

/** Find the most recent active meeting message in a channel's loaded messages. */
export const findActiveChannelMeeting = (messages = []) => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (isMeetingActive(msg?.meetingInfo)) {
      return msg;
    }
  }
  return null;
};

export const isMeetingOrganizer = (meetingInfo, userId) =>
  Boolean(meetingInfo?.startedByUserId && userId && meetingInfo.startedByUserId === userId);

export const getParticipantNotes = (meetingInfo) =>
  meetingInfo?.participantNotes || [];

export const getUserMeetingNote = (meetingInfo, userId) =>
  getParticipantNotes(meetingInfo).find((n) => n.userId === userId) || null;

export const buildAggregatedNotesPreview = (participantNotes = []) => {
  if (!participantNotes.length) return '';
  return participantNotes
    .filter((n) => n?.notesHtml?.trim())
    .map((n) => {
      const name = n.userName || 'Participant';
      return `<p><strong>${name}</strong></p>${n.notesHtml}`;
    })
    .join('');
};
